import type { Clinic } from "services/types/clinics.type";
import type {
  EmailBranding,
  EmailBrandingKind,
  EmailBrandingParts
} from "services/types/email-branding.type";

import { DEFAULT_DISCLAIMER_TEXT, TODAYS_PARTS } from "./email-branding.utils";

/** The clinic fields the frame reads: the logo, and the values a signature fills in. */
export const EMAIL_BRANDING_FRAME_CLINIC_QUERY =
  "{id,name,display_name,logo,phone,email,street_address_1,street_address_2,city}";

export type FrameClinic = Pick<
  Clinic,
  | "id"
  | "name"
  | "display_name"
  | "logo"
  | "phone"
  | "email"
  | "street_address_1"
  | "street_address_2"
  | "city"
>;

/** Who the email is sent as. Null: nobody known (automated mail, or a template used by anyone). */
export interface FrameSender {
  name: string;
  title: string;
}

export interface EmailBrandingFrameParts {
  logoUrl: string | null;
  /** Sanitize before rendering: it is rich text a practice admin wrote. */
  signatureHtml: string | null;
  disclaimerText: string | null;
}

const NO_PARTS = { logoUrl: null, signatureHtml: null, disclaimerText: null };

const ALL_PARTS: EmailBrandingParts = {
  logo: true,
  signature: true,
  disclaimer: true
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Shown in place of a staff field when nobody in particular sends the email. */
export const SENDER_PLACEHOLDERS = {
  staff_name: "(sender's name)",
  staff_title: "(sender's title)"
} as const;

/** `display_phone_prop`: 10 digits read as 555-555-5555, anything else as stored. */
const displayPhone = (phone: string | undefined) =>
  phone && /^\d{10}$/.test(phone)
    ? `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`
    : (phone ?? "");

/**
 * The backend's `fill_signature` (communication/email_branding.py), field for field, so the frame
 * reads like the sent email. Literal replacement: any other brace in the signature stays as written.
 */
export const fillSignature = (
  signatureHtml: string,
  {
    clinic,
    practiceName,
    sender
  }: {
    clinic: FrameClinic | null;
    practiceName: string;
    sender: FrameSender | null;
  }
): string => {
  const values: Record<string, string> = {
    clinic_name: clinic?.display_name || clinic?.name || "",
    clinic_phone: displayPhone(clinic?.phone),
    clinic_address: clinic
      ? [clinic.street_address_1, clinic.street_address_2, clinic.city]
          .filter(Boolean)
          .join(", ")
      : "",
    clinic_email: clinic?.email ?? "",
    practice_name: practiceName
  };
  let html = signatureHtml;
  Object.entries(values).forEach(([key, value]) => {
    html = html.split(`{${key}}`).join(escapeHtml(value));
  });
  (["staff_name", "staff_title"] as const).forEach((key) => {
    const value = sender
      ? escapeHtml(key === "staff_name" ? sender.name : sender.title)
      : `<em>${SENDER_PLACEHOLDERS[key]}</em>`;
    html = html.split(`{${key}}`).join(value);
  });
  return html;
};

/**
 * What the frame draws around an editor: the same parts, from the same settings, that
 * `Email.render_html` adds. `kind: "any"` is a reusable template, drawn with every part as an
 * example; `undefined` is a type in no kind, which keeps today's parts.
 */
export const resolveFrameParts = ({
  branding,
  hasSavedSettings,
  kind,
  isDesigner,
  clinic,
  practiceLogo,
  practiceName,
  sender,
  body
}: {
  branding: EmailBranding;
  hasSavedSettings: boolean;
  kind: EmailBrandingKind | "any" | undefined;
  isDesigner: boolean;
  clinic: FrameClinic | null;
  practiceLogo: string | null | undefined;
  practiceName: string;
  sender: FrameSender | null;
  /** The classic editor's body, when known: a part its merge tag places is not added again. */
  body?: string | null;
}): EmailBrandingFrameParts => {
  const placed = placedBrandingTags(body);
  // The backend adds nothing to a Designer email of a type in no kind, or before the practice has
  // saved its settings (Email._has_known_branding_kind / has_branding_setting).
  if (isDesigner && (kind === undefined || !hasSavedSettings)) {
    return NO_PARTS;
  }

  const parts =
    kind === "any"
      ? ALL_PARTS
      : kind
        ? branding.categories[kind]
        : TODAYS_PARTS;
  const override = clinic ? branding.clinic_overrides[String(clinic.id)] : null;

  const logoUrl =
    parts.logo && !isDesigner
      ? (branding.logo_source === "clinic" && clinic?.logo) ||
        practiceLogo ||
        null
      : null;

  const rawSignature =
    override?.signature_html ?? branding.signature_html ?? "";
  const signatureHtml =
    parts.signature && !placed.has("email_signature") && rawSignature.trim()
      ? fillSignature(rawSignature, { clinic, practiceName, sender })
      : null;

  const disclaimerText =
    parts.disclaimer && !placed.has("email_disclaimer")
      ? (override?.disclaimer_text ?? branding.disclaimer_text) ||
        DEFAULT_DISCLAIMER_TEXT
      : null;

  return { logoUrl, signatureHtml, disclaimerText };
};

// `{email_signature}` in a classic body, `<!--SUNO:email_signature-->` (or the bracket-less form
// Unlayer stores) in a Designer one: the backend's BRANDING_TAG_RE.
const BRANDING_TAG_RE =
  /\{(email_signature|email_disclaimer)\}|<?!--SUNO:(email_signature|email_disclaimer)-->?/g;

export const placedBrandingTags = (body: string | null | undefined) =>
  new Set(
    Array.from((body ?? "").matchAll(BRANDING_TAG_RE), (m) => m[1] ?? m[2])
  );

export const hasFrameParts = (parts: EmailBrandingFrameParts) =>
  Boolean(parts.logoUrl || parts.signatureHtml || parts.disclaimerText);
